import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useRef } from 'react';

type ParamConfig = Record<string, {
  default: string;
  parse?: (v: string) => any;
  serialize?: (v: any) => string;
}>;

export function useQueryParams<T extends ParamConfig>(config: T) {
  // Stabilize config reference to avoid re-renders
  const configRef = useRef(config);
  configRef.current = config;

  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const result: Record<string, any> = {};
    for (const [key, cfg] of Object.entries(configRef.current)) {
      const raw = searchParams.get(key) ?? cfg.default;
      result[key] = cfg.parse ? cfg.parse(raw) : raw;
    }
    return result as { [K in keyof T]: any };
  }, [searchParams]);

  const set = useCallback((updates: Partial<Record<keyof T, any>>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, val] of Object.entries(updates)) {
        const cfg = configRef.current[key];
        if (!cfg) continue;
        const serialized = cfg.serialize ? cfg.serialize(val) : String(val ?? '');
        if (serialized === cfg.default || serialized === '' || val === null || val === undefined) {
          next.delete(key);
        } else {
          next.set(key, serialized);
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return [values, set] as const;
}
